import { trpc } from "@/trpc/client";
import { guestAccessFromSession } from "@/utils/guestAccess";
import {
  browserGroupBy,
  campaignIdFrom,
  channelIdFrom,
  placementIdFrom,
  campaignListInput,
  readDateRange,
  shapeBrowsers,
  shapeCampaignRow,
  shapeLocations,
  siteIdFrom,
  wantsBrazil,
} from "@/trpc/listFilter";

function hasCampaign(variables) {
  return Boolean(campaignIdFrom(variables) || campaignIdFromLocation());
}

export async function listCampaigns(variables) {
  
    const guest = guestAccessFromSession();
    const input = campaignListInput(variables);
    if (guest) {
      input.campaign_ids = [guest.campaign_id];
      input.site_id = guest.site_id;
      input.offset = 0;
    }
    const result = await trpc.lookups.campaigns.list.query(input);
    const rows = result.rows.map(shapeCampaignRow);
    if (!guest) return rows;
    return rows.filter((row) => Number(row.campaign_id) === guest.campaign_id);
  
;
}

export async function countCampaigns(variables) {
  
    const guest = guestAccessFromSession();
    const input = campaignListInput(variables);
    if (guest) {
      input.campaign_ids = [guest.campaign_id];
      input.site_id = guest.site_id;
      input.offset = 0;
    } else {
      input.limit = 1;
      input.offset = 0;
    }
    const result = await trpc.lookups.campaigns.list.query(input);
    if (!guest) return Number(result.total) || 0;
    return result.rows.filter((row) => Number(row.campaign_id) === guest.campaign_id).length;
  
;
}

export async function listSites(variables) {
  
    const pageSize = 500;
    const rows = [];
    let offset = 0;
    let total = Infinity;
    while (offset < total && rows.length < 2000) {
      const input = { limit: pageSize, offset };
      if (variables?.search) input.search = String(variables.search);
      const result = await trpc.lookups.sites.list.query(input);
      total = Number(result.total) || 0;
      rows.push(...result.rows);
      if (!result.rows.length || result.rows.length < pageSize) break;
      offset += pageSize;
    }
    return rows;
  
;
}

export async function listCampaignSites(variables) {
  const campaignId = campaignIdFrom(variables);
  if (!campaignId) throw Object.assign(new Error("parâmetros ausentes"), { data: { code: "BAD_REQUEST" } });
  
    const siteId = siteIdFrom(variables);
    const result = await trpc.lookups.sites.list.query({
      limit: 500,
      offset: 0,
      campaign_ids: [campaignId],
    });
    if (!siteId) return result.rows;
    return result.rows.filter((row) => Number(row.site_id) === siteId);
  
;
}

async function devicesQuery(variables) {
  const input = await analyticsInput(variables);
  if (!input) {
    throw Object.assign(new Error("janela ou campanha ausente"), { data: { code: "BAD_REQUEST" } });
  }
  const groupBy = browserGroupBy(variables.group);
  const result = await trpc.analytics.devices.query({
    campaign_id: input.campaign_id,
    group_by: groupBy,
    start_datetime: input.start_datetime,
    end_datetime: input.end_datetime,
    ...(input.timezone ? { timezone: input.timezone } : {}),
    ...(input.site_id ? { site_id: input.site_id } : {}),
    live: true,
    limit: 100,
  });
  return shapeBrowsers(result.rows, groupBy);
}

export async function campaignBrowsers(variables) {
  if (!hasCampaign(variables)) throw Object.assign(new Error("parâmetros ausentes"), { data: { code: "BAD_REQUEST" } });
  return await devicesQuery(variables);
}

export async function siteBrowsers(variables) {
  if (!hasCampaign(variables)) throw Object.assign(new Error("parâmetros ausentes"), { data: { code: "BAD_REQUEST" } });
  return await devicesQuery(variables);
}

async function locationsQuery(variables, { brazil, groupBy }) {
  const input = await analyticsInput(variables);
  if (!input) {
    throw Object.assign(new Error("janela ou campanha ausente"), { data: { code: "BAD_REQUEST" } });
  }
  const result = await trpc.analytics.locations.query({
    campaign_id: input.campaign_id,
    group_by: groupBy,
    start_datetime: input.start_datetime,
    end_datetime: input.end_datetime,
    ...(input.timezone ? { timezone: input.timezone } : {}),
    ...(input.site_id ? { site_id: input.site_id } : {}),
    live: true,
    limit: 500,
  });
  const rows = brazil
    ? result.rows.filter((row) => /^(br|bra|brasil|brazil)$/i.test(String(row.country || "")))
    : result.rows;
  return shapeLocations(rows);
}

export async function campaignLocations(variables) {
  if (!hasCampaign(variables)) throw Object.assign(new Error("parâmetros ausentes"), { data: { code: "BAD_REQUEST" } });
  const brazil = wantsBrazil(variables);
  return await locationsQuery(variables, { brazil, groupBy: brazil ? "country_region" : "country" });
}

export async function locationSites(variables) {
  const input = await analyticsInput(variables);
  if (!input) return { campaign: { sites: [] } };
  const result = await trpc.analytics.locationSites.query({ ...input, live: true });
  return {
    campaign: {
      sites: (result?.sites || []).map((site) => ({
        site_id: site.site_id,
        site_name: site.site_name,
        locations: (site.locations || []).map((row) => ({
          _id: { country: row.country || "" },
          impressions: row.impressions || 0,
          views: row.views || 0,
          clicks: row.clicks || 0,
        })),
      })),
    },
  };
}

export async function urlSites(variables) {
  const input = await analyticsInput(variables);
  if (!input) return { sites: [] };
  const result = await trpc.analytics.urlSites.query({ ...input, live: true });
  return {
    sites: (result?.sites || []).map((site) => ({
      site_id: site.site_id,
      site_name: site.site_name,
      urls: (site.urls || []).map((row) => ({
        _id: { category: row.category || "" },
        impressions: row.impressions || 0,
        views: row.views || 0,
        clicks: row.clicks || 0,
      })),
    })),
  };
}

export async function reportRequests(variables) {
  const campaignId = campaignIdFrom(variables);
  if (!campaignId) return [];
  const siteId = siteIdFrom(variables);
  const result = await trpc.analytics.reportRequests.query({
    campaign_id: campaignId,
    ...(siteId ? { site_id: siteId } : {}),
  });
  return result?.rows || [];
}

function campaignIdFromLocation() {
  if (typeof window === "undefined") return null;
  const match = window.location.pathname.match(/campaign\/(\d+)/);
  return match ? Number(match[1]) : null;
}

function creativeIdFrom(variables = {}) {
  const direct = Number(variables.creativeId ?? variables.creative_id);
  if (Number.isInteger(direct) && direct > 0) return direct;
  const match = String(variables.filter || "").match(/creative_id['"]?\s*[:=]\s*['"]?(\d+)/i);
  return match ? Number(match[1]) : null;
}

function purchaseFormat(variables) {
  const raw = `${variables?.purchaseType || ""} ${variables?.channelsFilter2 || ""} ${variables?.purchaseTypePlacement || ""}`;
  const match = raw.match(/cpm|cpc|cpv/i);
  return match ? match[0].toLowerCase() : null;
}

async function mapPool(items, limit, fn) {
  const out = new Array(items.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      out[index] = await fn(items[index], index);
    }
  });
  await Promise.all(workers);
  return out;
}

function metricForFormat(format) {
  if (format === "cpc") return "clicks";
  if (format === "cpv") return "views";
  if (format === "cpe") return "completes";
  return "impressions";
}

function deliveredByMetric(row, metric) {
  if (metric === "clicks") return row?.clicks || 0;
  if (metric === "views") return row?.views || 0;
  if (metric === "completes") return row?.completes || 0;
  if (metric === "viewables") return row?.viewables || 0;
  return row?.impressions || 0;
}

/** Mesma forma de `siteChannels`: frações 0–1 e válidas de `derivedMetrics`. */
function totalFromLevel(row, format, metric) {
  const impressions = row?.impressions || 0;
  const views = row?.views || 0;
  const viewables = row?.viewables || 0;
  const blocklists = row?.blocklists || 0;
  const valids = Number.isFinite(Number(row?.valids))
    ? Number(row.valids)
    : views + (impressions - blocklists);
  const viewability = Number(row?.viewability) || 0;
  return {
    impressions,
    views,
    clicks: row?.clicks || 0,
    viewables,
    viewability,
    denominador_viewability: viewability > 0 ? viewables / viewability : 0,
    blocklists,
    blocklists_percent: impressions + views > 0 ? blocklists / (impressions + views) : 0,
    completes: row?.completes || 0,
    valids,
    delivered_quantity: deliveredByMetric(row, metric || metricForFormat(format)),
    delivery_percent: 0,
  };
}

function totalFromChannel(channel) {
  const impressions = channel.impressions || 0;
  const views = channel.views || 0;
  const viewables = channel.viewables || 0;
  const blocklists = channel.blocklists || 0;
  const quantity = channel.purchased_quantity || channel.channel_purchased_quantity || 0;
  const delivered = channel.delivered || 0;
  const viewability = Number(channel.va_percent ?? channel.viewability) || 0;
  return {
    impressions,
    views,
    clicks: channel.clicks || 0,
    viewables,
    viewability,
    denominador_viewability: viewability > 0 ? viewables / viewability : 0,
    blocklists,
    blocklists_percent: impressions + views > 0 ? blocklists / (impressions + views) : 0,
    completes: channel.completes || 0,
    valids: channel.valids || 0,
    delivered_quantity: delivered,
    delivery_percent: quantity > 0 ? delivered / quantity : Number(channel.delivery_percent) || 0,
  };
}

function weightedViewability(channels) {
  let weighted = 0;
  let base = 0;
  for (const channel of channels) {
    const delivered = channel.total_data?.delivered_quantity || 0;
    const va = Number(channel.total_data?.viewability);
    if (delivered > 0 && Number.isFinite(va)) {
      weighted += va * delivered;
      base += delivered;
    }
  }
  return base > 0 ? weighted / base : 0;
}

function sumTotals(rows) {
  return rows.reduce((acc, row) => {
    const data = row.total_data;
    acc.impressions += data.impressions;
    acc.views += data.views;
    acc.clicks += data.clicks;
    acc.viewables += data.viewables;
    acc.blocklists += data.blocklists;
    acc.completes += data.completes;
    acc.valids += data.valids;
    acc.delivered_quantity += data.delivered_quantity;
    return acc;
  }, {
    impressions: 0,
    views: 0,
    clicks: 0,
    viewables: 0,
    viewability: 0,
    blocklists: 0,
    blocklists_percent: 0,
    completes: 0,
    valids: 0,
    delivered_quantity: 0,
    delivery_percent: 0,
  });
}

function toUnix(value) {
  if (value == null) return null;
  if (value instanceof Date) {
    const n = Math.floor(value.getTime() / 1000);
    return n > 0 ? n : null;
  }
  if (typeof value === "number") {
    const n = value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
    return n > 0 ? n : null;
  }
  const parsed = Date.parse(String(value));
  if (!Number.isFinite(parsed)) return null;
  const n = Math.floor(parsed / 1000);
  return n > 0 ? n : null;
}

/** -03:00, o fuso default das procedures. Meia-noite local desse dia entra inteira no filtro. */
const ANALYTICS_TZ_OFFSET_MINUTES = -180;

/**
 * `campaign_*_datetime` é dia civil de contrato gravado como meia-noite UTC.
 * O componente UTC é a data (20/09), não o instante convertido para Brasília (19/09).
 */
function campaignCivilYmd(value) {
  if (value == null || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${date.getUTCFullYear()}-${month}-${day}`;
}

function civilYmdToUnix(ymd) {
  const [year, month, day] = ymd.split("-").map(Number);
  const utcMs = Date.UTC(year, month - 1, day, 0, 0, 0) - ANALYTICS_TZ_OFFSET_MINUTES * 60 * 1000;
  return Math.floor(utcMs / 1000);
}

/**
 * Sem data escolhida na tela, a entrega vem inteira.
 * O fim do contrato não entra aqui: evento depois de campaign_end continuava de fora.
 * O recorte só acontece em readDateRange, quando o usuário filtra.
 */
function openDeliveryWindow() {
  return {
    start_datetime: civilYmdToUnix("2000-01-01"),
    end_datetime: civilYmdToUnix("2099-01-01"),
  };
}

async function analyticsInput(variables) {
  const campaignId = campaignIdFrom(variables) || campaignIdFromLocation();
  if (!campaignId) return null;
  const range = readDateRange(variables) || openDeliveryWindow();
  if (!range) return null;
  const siteId = siteIdFrom(variables);
  return {
    campaign_id: campaignId,
    start_datetime: range.start_datetime,
    end_datetime: range.end_datetime,
    ...(range.timezone ? { timezone: range.timezone } : {}),
    ...(siteId ? { site_id: siteId } : {}),
  };
}

const placementCatalogCache = new Map();

async function placementCatalog(campaignId) {
  if (placementCatalogCache.has(campaignId)) return placementCatalogCache.get(campaignId);
  const [heatmapRows, listRows] = await Promise.all([
    trpc.lookups.placements.forHeatmap.query({ campaign_id: campaignId }),
    trpc.lookups.placements.listByCampaign.query({ campaign_id: campaignId }),
  ]);
  const byPlacement = new Map();
  for (const row of listRows || []) {
    byPlacement.set(Number(row.placement_id), {
      placement_id: Number(row.placement_id),
      placement_name: row.placement_name || "",
      placement_dates: row.placement_dates || "",
      placement_purchase_qty: Number(row.placement_purchase_qty) || 0,
      channel_id: row.channel_id != null ? Number(row.channel_id) : null,
      placement_status_id:
        row.placement_status_id != null ? Number(row.placement_status_id) : 0,
      format: {
        format_size_width: 0,
        format_size_height: 0,
        format_name: row.placement_name || "",
      },
      creatives: [],
    });
  }
  for (const row of heatmapRows || []) {
    const id = Number(row.placement_id);
    const prev = byPlacement.get(id) || {};
    byPlacement.set(id, {
      ...prev,
      ...row,
      placement_name: row.placement_name || prev.placement_name || "",
      channel_id:
        row.channel_id != null
          ? Number(row.channel_id)
          : prev.channel_id != null
            ? Number(prev.channel_id)
            : null,
      placement_status_id:
        row.placement_status_id != null
          ? Number(row.placement_status_id)
          : Number(prev.placement_status_id) || 0,
      placement_dates: row.placement_dates || prev.placement_dates || "",
      placement_purchase_qty:
        row.placement_purchase_qty != null
          ? Number(row.placement_purchase_qty)
          : Number(prev.placement_purchase_qty) || 0,
      format: row.format || prev.format,
      creatives: row.creatives || prev.creatives || [],
    });
  }
  placementCatalogCache.set(campaignId, byPlacement);
  return byPlacement;
}

function purchaseTypeShape(format) {
  const code = String(format || "").toLowerCase();
  return {
    purchase_type_format: code,
    purchase_type_name: code ? code.toUpperCase() : "",
  };
}

function totalForPurchase(row, format, metric) {
  return totalFromLevel(row, format, metric);
}

async function levelRows(input, level, parent) {
  const result = await trpc.analytics.byLevel.query({
    campaign_id: input.campaign_id,
    level,
    parent,
    start_datetime: input.start_datetime,
    end_datetime: input.end_datetime,
    ...(input.timezone ? { timezone: input.timezone } : {}),
    dataset: "creatives",
    apply_client_filters: false,
    live: true,
    limit: 500,
  });
  return result.rows || [];
}

async function loadSiteChannels(variables) {
  const input = await analyticsInput(variables);
  if (!input) {
    throw Object.assign(new Error("janela ou campanha ausente"), { data: { code: "BAD_REQUEST" } });
  }
  const channelResult = await trpc.analytics.siteChannels.query({
    campaign_id: input.campaign_id,
    start_datetime: input.start_datetime,
    end_datetime: input.end_datetime,
    ...(input.timezone ? { timezone: input.timezone } : {}),
    ...(input.site_id ? { site_id: input.site_id } : {}),
    live: true,
  });
  return { input, channels: channelResult.channels || [] };
}

function groupChannelsBySite(channels) {
  const bySite = new Map();
  for (const channel of channels) {
    if (!bySite.has(channel.site_id)) {
      bySite.set(channel.site_id, {
        site_id: channel.site_id,
        site_name: channel.site_name || "",
        channels: [],
      });
    }
    bySite.get(channel.site_id).channels.push({
      channel_id: channel.channel_id,
      channel_descr: channel.channel_descr || "",
      purchase_type: channel.purchase_type,
      client_metric: channel.client_metric,
      channel_purchased_quantity: channel.purchased_quantity || 0,
      placements: [],
      total_data: totalFromChannel(channel),
    });
  }
  return [...bySite.values()].filter((site) => site.channels.length > 0);
}

async function purchaseSites(variables) {
  const { input, channels: loaded } = await loadSiteChannels(variables);
  const format = purchaseFormat(variables);
  const channels = loaded.filter((channel) => !format || channel.purchase_type === format);
  const catalog = await placementCatalog(input.campaign_id);
  const bySite = new Map();
  for (const channel of channels) {
    if (!bySite.has(channel.site_id)) {
      bySite.set(channel.site_id, {
        site_id: channel.site_id,
        site_name: channel.site_name || "",
        channels: [],
      });
    }
    bySite.get(channel.site_id).channels.push(channel);
  }

  const sites = await mapPool([...bySite.values()], 4, async (site) => {
    const nested = await mapPool(site.channels, 4, async (channel) => {
      let placements = [];
      try {
        const placementRows = await levelRows(input, "placement", { channel_id: channel.channel_id });
        placements = await mapPool(placementRows, 4, async (placement) => {
          let creatives = [];
          try {
            const creativeRows = await levelRows(input, "creative", { placement_id: placement.id });
            creatives = creativeRows.map((creative) => ({
              creative_id: creative.id,
              creative_group: { group_name: creative.name || "" },
              total_data: totalForPurchase(creative, channel.purchase_type, channel.client_metric),
            }));
          } catch {
            creatives = [];
          }
          const meta = catalog.get(Number(placement.id));
          return {
            placement_id: placement.id,
            placement_name: placement.name || "",
            placement_dates: meta?.placement_dates || "",
            format: {
              format_size_width: meta?.format?.format_size_width || 0,
              format_size_height: meta?.format?.format_size_height || 0,
              format_name: meta?.format?.format_name || placement.name || "",
            },
            creatives,
            total_data: totalForPurchase(placement, channel.purchase_type, channel.client_metric),
          };
        });
      } catch {
        placements = [];
      }
      return {
        channel_id: channel.channel_id,
        channel_descr: channel.channel_descr || "",
        purchase_type: channel.purchase_type,
        channel_purchased_quantity: channel.purchased_quantity || 0,
        placements,
        total_data: totalFromChannel(channel),
      };
    });
    const total = sumTotals(nested);
    total.blocklists_percent = total.impressions > 0 ? (total.blocklists / total.impressions) * 100 : 0;
    return {
      site_id: site.site_id,
      site_name: site.site_name,
      channels: nested,
      total_data: total,
    };
  });

  return sites.filter((site) => site.channels.length > 0);
}

function primaryPurchaseFormat(channels) {
  let best = "cpm";
  let qty = -1;
  for (const format of ["cpm", "cpc", "cpv"]) {
    const total = channels
      .filter((channel) => channel.purchase_type === format)
      .reduce((sum, channel) => sum + (channel.channel_purchased_quantity || 0), 0);
    if (total > qty) {
      qty = total;
      best = format;
    }
  }
  return best;
}

function purchasesFromChannels(channels) {
  const purchases = {};
  for (const format of ["cpm", "cpc", "cpv"]) {
    const rows = channels.filter((channel) => channel.purchase_type === format);
    const quantity = rows.reduce((sum, channel) => sum + (channel.channel_purchased_quantity || 0), 0);
    const total_data = sumTotals(rows);
    total_data.blocklists_percent = total_data.impressions + total_data.views > 0
      ? total_data.blocklists / (total_data.impressions + total_data.views)
      : 0;
    total_data.viewability = weightedViewability(rows);
    total_data.denominador_viewability = total_data.viewability > 0
      ? total_data.viewables / total_data.viewability
      : 0;
    total_data.delivery_percent = quantity > 0 ? total_data.delivered_quantity / quantity : 0;
    purchases[format] = { quantity, channels: rows, total_data };
  }
  return purchases;
}

export async function campaignSiteRows(variables) {
  if (!hasCampaign(variables)) {
    throw Object.assign(new Error("parâmetros ausentes"), { data: { code: "BAD_REQUEST" } });
  }
  
    const { channels } = await loadSiteChannels(variables);
    return groupChannelsBySite(channels).map((site) => ({
      site_id: site.site_id,
      site_name: site.site_name,
      purchases: purchasesFromChannels(site.channels || []),
    }));
  
;
}

export async function campaignPurchaseTree(variables) {
  if (!hasCampaign(variables)) {
    throw Object.assign(new Error("parâmetros ausentes"), { data: { code: "BAD_REQUEST" } });
  }
  return await ({ sites: await purchaseSites(variables) });
}

function asViewabilityRatio(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n > 1 ? n / 100 : n;
}

function purchaseBucket(channels, format) {
  const rows = channels.filter((channel) => channel.purchase_type === format);
  const quantity = rows.reduce((sum, channel) => sum + (channel.purchased_quantity || 0), 0);
  const delivered = rows.reduce((sum, channel) => sum + (channel.delivered || 0), 0);
  const impressions = rows.reduce((sum, channel) => sum + (channel.impressions || 0), 0);
  const blocklists = rows.reduce((sum, channel) => sum + (channel.blocklists || 0), 0);
  const viewables = rows.reduce((sum, channel) => sum + (Number(channel.viewables) || 0), 0);
  let weighted = 0;
  let base = 0;
  for (const channel of rows) {
    const weight = Number(channel.delivered) || 0;
    const va = asViewabilityRatio(channel.va_percent ?? channel.viewability);
    if (weight > 0) {
      weighted += va * weight;
      base += weight;
    }
  }
  return {
    quantity,
    total_data: {
      impressions,
      clicks: rows.reduce((sum, channel) => sum + (channel.clicks || 0), 0),
      views: rows.reduce((sum, channel) => sum + (channel.views || 0), 0),
      viewables,
      viewability: base > 0 ? weighted / base : 0,
      completes: rows.reduce((sum, channel) => sum + (channel.completes || 0), 0),
      blocklists,
      blocklists_percent: impressions > 0 ? (blocklists / impressions) * 100 : 0,
      valids: rows.reduce((sum, channel) => sum + (channel.valids || 0), 0),
      delivered_quantity: delivered,
      delivery_percent: quantity > 0 ? delivered / quantity : 0,
    },
  };
}

export async function purchaseTotals(variables) {
  if (!hasCampaign(variables)) throw Object.assign(new Error("parâmetros ausentes"), { data: { code: "BAD_REQUEST" } });
  
  const input = await analyticsInput(variables);
  if (!input) {
    throw Object.assign(new Error("janela ou campanha ausente"), { data: { code: "BAD_REQUEST" } });
  }
    const channelResult = await trpc.analytics.siteChannels.query({
      campaign_id: input.campaign_id,
      start_datetime: input.start_datetime,
      end_datetime: input.end_datetime,
      ...(input.timezone ? { timezone: input.timezone } : {}),
      ...(input.site_id ? { site_id: input.site_id } : {}),
      live: true,
    });
    const channels = channelResult.channels || [];
    const purchases = {
      cpm: purchaseBucket(channels, "cpm"),
      cpc: purchaseBucket(channels, "cpc"),
      cpv: purchaseBucket(channels, "cpv"),
    };
    const total_data = sumTotals([
      { total_data: purchases.cpm.total_data },
      { total_data: purchases.cpc.total_data },
      { total_data: purchases.cpv.total_data },
    ]);
    total_data.viewability = total_data.valids > 0 ? Math.min(1, total_data.viewables / total_data.valids) : 0;
    total_data.blocklists_percent = total_data.impressions > 0 ? (total_data.blocklists / total_data.impressions) * 100 : 0;
    return { purchases, total_data };
  
;
}

function brDate(iso) {
  const [year, month, day] = String(iso || "").slice(0, 10).split("-");
  if (!year || !month || !day) return iso;
  return `${day}/${month}/${year}`;
}

function formatCampaignDate(value) {
  const ymd = campaignCivilYmd(value);
  if (!ymd) return value == null || value === "" ? "" : String(value);
  const [year, month, day] = ymd.split("-");
  return `${day}/${month}/${year}`;
}

export async function campaignHeader(variables) {
  const campaignId = campaignIdFrom(variables) || campaignIdFromLocation();
  if (!campaignId) return null;
  const row = await trpc.lookups.campaigns.get.query({ campaign_id: campaignId });
  let teamAnalyticsUrl = "";
  if (row.team_id) {
    const team = await trpc.lookups.team.resolve.query({ team_id: row.team_id });
    teamAnalyticsUrl = team?.team_analytics_url || "";
  }
  return {
    campaign_id: row.campaign_id,
    campaign_name: row.campaign_name || "",
    account_id: row.account_id ?? null,
    campaign_status_id: row.campaign_status_id ?? null,
    campaign_start_datetime: formatCampaignDate(row.campaign_start_datetime),
    campaign_end_datetime: formatCampaignDate(row.campaign_end_datetime),
    account: { account_name: row.account?.account_name || "" },
    advertiser: { account_name: row.advertiser?.account_name || "" },
    team: {
      team_id: row.team_id ?? row.team?.team_id ?? null,
      team_analytics_url: teamAnalyticsUrl,
    },
  };
}

function emptyDailyGroup() {
  return {
    impressions: 0,
    clicks: 0,
    views: 0,
    viewables: 0,
    valids: 0,
    blocklists: 0,
    completes: 0,
    delivered_quantity: 0,
    vaWeighted: 0,
    vaBase: 0,
    channels: {},
  };
}

function finishDailyGroup(group, quantity) {
  const viewability = group.vaBase > 0 ? group.vaWeighted / group.vaBase : 0;
  const delivery_percent = quantity > 0 ? group.delivered_quantity / quantity : 0;
  return {
    impressions: group.impressions,
    clicks: group.clicks,
    views: group.views,
    viewables: group.viewables,
    valids: group.valids,
    blocklists: group.blocklists,
    completes: group.completes,
    delivered_quantity: group.delivered_quantity,
    viewability,
    delivery_percent,
    total_data_purchase_type_channels: group.channels,
  };
}

export async function channelPlacements(variables) {
  const campaignId = campaignIdFrom(variables) || campaignIdFromLocation();
  const channelId = channelIdFrom(variables);
  if (!campaignId || !channelId) throw Object.assign(new Error("parâmetros ausentes"), { data: { code: "BAD_REQUEST" } });
  const format = String(variables.purchaseType || variables.purchase_type || "").toLowerCase();
  const metric = variables.clientMetric || variables.client_metric;
  
    const input = await analyticsInput(variables);
    if (!input) return [];
    const [rows, catalog] = await Promise.all([
      levelRows(input, "placement", { channel_id: channelId }),
      placementCatalog(campaignId),
    ]);
    const purchaseType = purchaseTypeShape(format);
    const metricsById = new Map(
      (rows || []).map((row) => [Number(row.id), row])
    );
    const catalogIds = [...catalog.values()]
      .filter((meta) => Number(meta.channel_id) === Number(channelId))
      .map((meta) => Number(meta.placement_id));
    const placementIds = [
      ...new Set([...catalogIds, ...metricsById.keys()]),
    ].sort((a, b) => b - a);

    return placementIds.map((placementId) => {
      const meta = catalog.get(placementId);
      const row = metricsById.get(placementId) || {
        id: placementId,
        name: meta?.placement_name || "",
      };
      return {
        placement_id: placementId,
        placement_name: row.name || meta?.placement_name || "",
        placement_dates: meta?.placement_dates || "",
        placement_purchase_qty: Number(meta?.placement_purchase_qty) || 0,
        client_metric: metric || metricForFormat(format),
        format: {
          format_size_width: meta?.format?.format_size_width || 0,
          format_size_height: meta?.format?.format_size_height || 0,
          format_name: meta?.format?.format_name || row.name || meta?.placement_name || "",
        },
        purchase_type: purchaseType,
        placement_status: {
          placement_status_id: Number(meta?.placement_status_id) || 0,
          status_descr: "",
        },
        total_data: totalForPurchase(row, format, metric),
        creatives: [],
      };
    });
  
;
}

export async function placementCreatives(variables) {
  const campaignId = campaignIdFrom(variables) || campaignIdFromLocation();
  const placementId = placementIdFrom(variables);
  if (!campaignId || !placementId) throw Object.assign(new Error("parâmetros ausentes"), { data: { code: "BAD_REQUEST" } });
  const format = String(variables.purchaseType || variables.purchase_type || "").toLowerCase();
  const metric = variables.clientMetric || variables.client_metric;
  
    const input = await analyticsInput(variables);
    if (!input) return [];
    const [rows, catalog] = await Promise.all([
      levelRows(input, "creative", { placement_id: placementId }),
      placementCatalog(campaignId),
    ]);
    const meta = catalog.get(placementId);
    const catalogCreatives = meta?.creatives || [];
    const groups = new Map(
      catalogCreatives.map((creative) => [Number(creative.creative_id), creative.creative_group?.group_name || ""])
    );
    const metricsById = new Map((rows || []).map((row) => [Number(row.id), row]));
    const creativeIds = [
      ...new Set([
        ...catalogCreatives.map((creative) => Number(creative.creative_id)),
        ...metricsById.keys(),
      ]),
    ]
      .filter((id) => Number.isFinite(id) && id > 0)
      .sort((a, b) => b - a);
    const purchaseType = purchaseTypeShape(format);
    return creativeIds.map((creativeId) => {
      const row = metricsById.get(creativeId) || { id: creativeId, name: "" };
      return {
        creative_id: creativeId,
        creative_group: { group_name: groups.get(creativeId) || row.name || "" },
        placement: { purchase_type: purchaseType },
        total_data: totalForPurchase(row, format, metric),
      };
    });
  
;
}

export async function dailySites(variables) {
  if (!hasCampaign(variables)) throw Object.assign(new Error("parâmetros ausentes"), { data: { code: "BAD_REQUEST" } });
  
    const { input, channels } = await loadSiteChannels(variables);
    const channelById = new Map(channels.map((channel) => [Number(channel.channel_id), channel]));
    const sites = groupChannelsBySite(channels);
    const daily = await trpc.analytics.daily.query({
      campaign_id: input.campaign_id,
      level: "channel",
      dataset: "creatives",
      parent: input.site_id ? { site_id: input.site_id } : {},
      start_datetime: input.start_datetime,
      end_datetime: input.end_datetime,
      ...(input.timezone ? { timezone: input.timezone } : {}),
      live: true,
      limit: 2000,
    });
    const bySiteDate = new Map();
    for (const row of daily.rows || []) {
      const channel = channelById.get(Number(row.level_id));
      const format = channel?.purchase_type;
      if (!channel || (format !== "cpm" && format !== "cpc" && format !== "cpv")) continue;
      if (!bySiteDate.has(channel.site_id)) bySiteDate.set(channel.site_id, new Map());
      const byDate = bySiteDate.get(channel.site_id);
      if (!byDate.has(row.date)) {
        byDate.set(row.date, { cpm: emptyDailyGroup(), cpc: emptyDailyGroup(), cpv: emptyDailyGroup() });
      }
      const group = byDate.get(row.date)[format];
      const impressions = row.impressions || 0;
      const clicks = row.clicks || 0;
      const views = row.views || 0;
      const viewables = row.viewables || 0;
      const completes = row.completes || 0;
      const delivered = format === "cpc" ? clicks : format === "cpv" ? views : impressions;
      const quantity = channel.purchased_quantity || channel.channel_purchased_quantity || 0;
      const viewability = asViewabilityRatio(row.viewability);
      group.channels[channel.channel_id] = {
        channel_id: channel.channel_id,
        channel_descr: channel.channel_descr || "",
        channel_purchased_quantity: quantity,
        purchase_type: format,
        impressions,
        clicks,
        views,
        viewables,
        valids: impressions,
        blocklists: 0,
        completes,
        delivered_quantity: delivered,
        delivery_percent: quantity > 0 ? delivered / quantity : 0,
        viewability,
      };
      if (delivered > 0) {
        group.vaWeighted += viewability * delivered;
        group.vaBase += delivered;
      }
      group.impressions += impressions;
      group.clicks += clicks;
      group.views += views;
      group.viewables += viewables;
      group.completes += completes;
      group.valids += impressions;
      group.delivered_quantity += delivered;
    }
    return sites.map((site) => {
      const byDate = bySiteDate.get(site.site_id) || new Map();
      const contracted = (format) => site.channels
        .filter((channel) => channel.purchase_type === format)
        .reduce((sum, channel) => sum + (channel.channel_purchased_quantity || channel.purchased_quantity || 0), 0);
      const purchases = {
        cpm: { quantity: contracted("cpm") },
        cpc: { quantity: contracted("cpc") },
        cpv: { quantity: contracted("cpv") },
      };
      return {
        site_id: site.site_id,
        site_name: site.site_name,
        purchases,
        data_by_date_purchase: [...byDate.entries()].map(([date, groups]) => ({
          datetime: brDate(date),
          cpm: finishDailyGroup(groups.cpm, purchases.cpm.quantity),
          cpc: finishDailyGroup(groups.cpc, purchases.cpc.quantity),
          cpv: finishDailyGroup(groups.cpv, purchases.cpv.quantity),
        })),
      };
    });
  
;
}

export async function campaignPlacements(variables) {
  const campaignId = campaignIdFrom(variables) || campaignIdFromLocation();
  if (!campaignId) throw Object.assign(new Error("parâmetros ausentes"), { data: { code: "BAD_REQUEST" } });
  const siteId = siteIdFrom({
    filter: variables.placementsFilter2 || variables.filter,
    site_id: variables.site_id,
  });
  
    return trpc.lookups.placements.forHeatmap.query({
      campaign_id: campaignId,
      ...(siteId ? { site_id: siteId } : {}),
    });
  
;
}

export async function creativeHeatmaps(variables) {
  const creativeId = creativeIdFrom(variables);
  const campaignId = campaignIdFrom(variables) || campaignIdFromLocation();
  if (!creativeId || !campaignId) throw Object.assign(new Error("parâmetros ausentes"), { data: { code: "BAD_REQUEST" } });
  const range = readDateRange(variables);
  
    const result = await trpc.analytics.heatmap.query({
      campaign_id: campaignId,
      creative_id: creativeId,
      ...(range
        ? {
          start_datetime: range.start_datetime,
          end_datetime: range.end_datetime,
          ...(range.timezone ? { timezone: range.timezone } : {}),
        }
        : {}),
      live: true,
    });
    return [{
      placement: { purchase_type: { purchase_type_name: "" } },
      heatmaps: (result.points || []).map((point) => ({
        _id: { x: point.x, y: point.y },
        move: point.count,
      })),
    }];
  
;
}

export async function clearTrpcSession() {
  try {
    await trpc.auth.logout.mutate();
  } catch {
    /* cookie ausente */
  }
}
